import moment from "moment";

const REFERENCE_DAY = "1970-01-01";

// Parses "HH:mm" (or "HH:mm:ss") onto a fixed reference day, no day-wrap applied yet.
export function parseTimeOfDay(hhmm) {
    return moment(`${REFERENCE_DAY} ${hhmm || "00:00"}`, "YYYY-MM-DD HH:mm:ss");
}

// A schedule is a single ~24h cycle starting at its anchor (13:00 for RTC-based, 00:00 for
// Time-based). A slot's time-of-day belongs to "the next day" iff it falls earlier in the day
// than the anchor - the same single-wraparound rule the chart already uses (see AstroDim5.js's
// getNumberFromHour), generalized here to any anchor rather than a hardcoded 13:00.
export function resolveSlotTime(anchorHHmm, hhmm) {
    const anchor = parseTimeOfDay(anchorHHmm);
    const candidate = parseTimeOfDay(hhmm);
    return candidate.isBefore(anchor) ? candidate.add(1, "day") : candidate;
}

// Resolves every slot (in order) to its absolute moment via the anchor-relative rule above.
// Each slot resolves independently from the anchor (not chained off neighboring slots' values),
// so a slot's bounds can be computed from its neighbors regardless of edit order.
export function resolveAstroDimChain(anchorHHmm, slotHHmm) {
    return slotHHmm.map((hhmm) => resolveSlotTime(anchorHHmm, hhmm));
}

// Exclusive lower boundary for slot `index` (0-based into the slot array / resolvedChain):
// the anchor itself for the first slot, otherwise the previous slot's resolved time.
export function getSlotMinExclusive(anchorHHmm, resolvedChain, index) {
    return index === 0 ? parseTimeOfDay(anchorHHmm) : resolvedChain[index - 1];
}

// Exclusive upper boundary for slot `index`: the next slot's resolved time, or undefined for
// the last slot (nothing to be lower than).
export function getSlotMaxExclusive(resolvedChain, index) {
    return index === resolvedChain.length - 1 ? undefined : resolvedChain[index + 1];
}

// Hard clamp: commit the entry as-is if its anchor-relative resolution lands strictly between
// the two boundaries, otherwise clamp to whichever boundary it violates (+/- 1 minute).
// Deliberately does NOT keep wrapping the entry forward day after day to "find" a fit - a value
// that can't be placed within this schedule's single day/next-day cycle is out of range, not a
// further rollover. If the two boundaries are only a minute apart there's no valid value between
// them; clamping to the lower boundary in that degenerate case is a reasonable best effort - the
// backend's own ordering validation remains the final safety net regardless.
export function clampToRange(anchorHHmm, hhmm, minExclusive, maxExclusive) {
    const resolved = resolveSlotTime(anchorHHmm, hhmm);
    if (minExclusive && !resolved.isAfter(minExclusive)) {
        return minExclusive.clone().add(1, "minute").format("HH:mm");
    }
    if (maxExclusive && !resolved.isBefore(maxExclusive)) {
        return maxExclusive.clone().subtract(1, "minute").format("HH:mm");
    }
    return hhmm;
}

// Whether a resolved moment rolled onto a later calendar day than the reference day.
export function isNextDay(resolvedMoment) {
    return resolvedMoment.date() !== moment(REFERENCE_DAY).date();
}
